/**
 * `<meld-tray>`: a team's melds or books, spread out flat for reading.
 *
 * The felt shows piles turned to face their owners and squeezed to fit. This
 * is the same tableau laid out level, with each meld's cards, its PURE or WILD
 * badge spelled out and its points, opened by clicking any pile on the table.
 */

import {
    type Meld,
    type MeldableRank,
    type Team,
    getMeldValue,
    isBook,
    isPure
} from "@ryzun/haf-saloon-game-engine";
import type { TeamColor } from "../../server/protocol.js";
import { getRankWord } from "../cards.js";
import { h } from "../dom.js";
import { type TableauKind, emit } from "../events.js";
import { describeMeld, selectMelds } from "../meld-text.js";
import { BaseElement } from "./base-element.js";
import styles from "./meld-tray.css";
import "./playing-card.js";

export interface TrayModel {
    readonly team: Team;
    readonly kind: TableauKind;
    readonly name: string;
    readonly color: TeamColor;
    /** Whether this is the viewer's own team; only their melds can be played into. */
    readonly isOwn: boolean;
    readonly addableRanks: readonly MeldableRank[];
}

function renderMeldBody(meld: Meld): readonly HTMLElement[] {
    const cards = h("span", { "class": "cards", "aria-hidden": "true" }, ...meld.cards.map((card) => {
        const face = document.createElement("playing-card");
        face.card = card;

        return face;
    }));

    const badge = isBook(meld)
        ? h("span", { class: isPure(meld) ? "badge pure" : "badge" }, isPure(meld) ? "PURE" : "WILD")
        : null;

    return [
        h("span", { class: "rank" }, getRankWord(meld.rank, true)),
        cards,
        h("span", { class: "meta" }, badge, h("span", { class: "points" }, String(getMeldValue(meld))))
    ];
}

export class MeldTray extends BaseElement {
    #model: TrayModel | null = null;

    constructor() {
        super(styles);
    }

    set model(value: TrayModel | null) {
        this.#model = value;
        this.invalidate();
    }

    protected render(): void {
        const model = this.#model;

        if (model === null) {
            this.root.replaceChildren();
            return;
        }

        this.style.setProperty("--team", `var(--team-${model.color})`);

        const melds = selectMelds(model.team.melds, model.kind);
        const heading = h(
            "h3",
            { class: "heading" },
            model.name,
            model.team.inGame ? h("span", { class: "chip" }, "in") : h("span", { class: "chip out" }, "not in")
        );

        if (melds.length === 0) {
            this.root.replaceChildren(heading, h("p", { class: "empty" }, "Nothing on the table yet."));
            return;
        }

        this.root.replaceChildren(heading, h("ul", { class: "melds" }, ...melds.map((meld) => h(
            "li",
            {},
            this.#renderMeld(model, meld)
        ))));
    }

    #renderMeld(model: TrayModel, meld: Meld): HTMLElement {
        // Only your own melds can be played into, so an opponent's is a reading
        // of the table rather than a control.
        if (!model.isOwn) {
            return h(
                "div",
                { "class": "meld", "role": "group", "aria-label": `${model.name}, ${describeMeld(meld)}` },
                ...renderMeldBody(meld)
            );
        }

        const addable = model.addableRanks.includes(meld.rank);
        const button = h(
            "button",
            {
                "type": "button",
                "class": addable ? "meld addable" : "meld",
                "disabled": !addable,
                "aria-label": addable
                    ? `Add selected cards to your team's ${describeMeld(meld)}`
                    : `${model.name}, ${describeMeld(meld)}`
            },
            ...renderMeldBody(meld)
        );

        button.addEventListener("click", () => emit(this, "meld-add", { rank: meld.rank }));

        return button;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "meld-tray": MeldTray;
    }
}

customElements.define("meld-tray", MeldTray);
